import api from '../../utils/api';
import { vendorRoleMapping } from './vendorService';

export type LegalAudience = 'USER' | 'VENDOR';
export type LegalCategory =
  | 'TERMS_AND_CONDITIONS'
  | 'PRIVACY_POLICY'
  | 'CANCELLATION_AND_REFUND'
  | 'SHIPPING_POLICY'
  | 'VENDOR_REFUND_POLICY';

/** Fixed display order for category dropdowns across list / create / edit */
export const LEGAL_POLICY_CATEGORY_KEYS: LegalCategory[] = [
  'PRIVACY_POLICY',
  'TERMS_AND_CONDITIONS',
  'CANCELLATION_AND_REFUND',
  'SHIPPING_POLICY',
  'VENDOR_REFUND_POLICY',
];

/**
 * Categories valid per audience for create / edit dropdowns.
 *  - USER  : the four general policies that apply to end users (app + web).
 *  - VENDOR: vendor-specific policies only.
 */
export const LEGAL_CATEGORIES_BY_AUDIENCE: Record<LegalAudience, LegalCategory[]> = {
  USER: ['PRIVACY_POLICY', 'TERMS_AND_CONDITIONS', 'CANCELLATION_AND_REFUND', 'SHIPPING_POLICY'],
  VENDOR: ['VENDOR_REFUND_POLICY'],
};

export const LEGAL_POLICY_SCOPED_ALL = -1;

/** Read a picked .md file as UTF-8 text for the legal policy editor. */
export async function readMarkdownFileAsText(file: File): Promise<string> {
  return file.text();
}

export interface LegalPolicyVersionDto {
  id: string;
  audience: LegalAudience;
  category: LegalCategory;
  scopedVendorType: number;
  version: number;
  title: string | null;
  markdownContent: string;
  isPublished: boolean;
  publishedAt: string | null;
  createdByMasterAdminId: number | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListLegalPoliciesParams {
  audience?: LegalAudience;
  category?: LegalCategory;
  scopedVendorType?: number;
  isPublished?: boolean;
}

const stripMultipartContentType = (data: unknown, headers: Record<string, string>) => {
  if (data instanceof FormData) {
    delete headers['Content-Type'];
  }
  return data as FormData;
};

export async function fetchLegalPolicyVersions(
  params?: ListLegalPoliciesParams
): Promise<{ success: boolean; data: LegalPolicyVersionDto[] }> {
  const response = await api.get<{ success: boolean; data: LegalPolicyVersionDto[] }>(
    '/master-admin/legal-policy-versions',
    { params }
  );
  return response.data;
}

export async function fetchLegalPolicyVersionById(
  id: string
): Promise<{ success: boolean; data: LegalPolicyVersionDto }> {
  const response = await api.get<{ success: boolean; data: LegalPolicyVersionDto }>(
    `/master-admin/legal-policy-versions/${encodeURIComponent(id)}`
  );
  return response.data;
}

export interface CreateLegalPolicyPayload {
  audience: LegalAudience;
  category: LegalCategory;
  scopedVendorType?: number;
  title?: string;
  markdownContent?: string;
  publishNow?: boolean;
}

export async function createLegalPolicyVersion(
  payload: CreateLegalPolicyPayload,
  file?: File | null
): Promise<{ success: boolean; data: LegalPolicyVersionDto }> {
  if (file) {
    const fd = new FormData();
    fd.append('audience', payload.audience);
    fd.append('category', payload.category);
    fd.append(
      'scopedVendorType',
      String(
        payload.audience === 'VENDOR'
          ? payload.scopedVendorType ?? LEGAL_POLICY_SCOPED_ALL
          : LEGAL_POLICY_SCOPED_ALL
      )
    );
    if (payload.title) fd.append('title', payload.title);
    fd.append('publishNow', String(Boolean(payload.publishNow)));
    fd.append('policyFile', file);
    const response = await api.post<{ success: boolean; data: LegalPolicyVersionDto }>(
      '/master-admin/legal-policy-versions',
      fd,
      {
        transformRequest: [stripMultipartContentType],
      }
    );
    return response.data;
  }

  const response = await api.post<{ success: boolean; data: LegalPolicyVersionDto }>(
    '/master-admin/legal-policy-versions',
    {
      audience: payload.audience,
      category: payload.category,
      scopedVendorType:
        payload.audience === 'VENDOR'
          ? payload.scopedVendorType ?? LEGAL_POLICY_SCOPED_ALL
          : LEGAL_POLICY_SCOPED_ALL,
      title: payload.title || null,
      markdownContent: payload.markdownContent || '',
      publishNow: Boolean(payload.publishNow),
    }
  );
  return response.data;
}

export async function updateLegalPolicyDraft(
  id: string,
  payload: { title?: string; markdownContent?: string },
  file?: File | null
): Promise<{ success: boolean; data: LegalPolicyVersionDto }> {
  if (file) {
    const fd = new FormData();
    if (payload.title !== undefined) fd.append('title', payload.title);
    fd.append('policyFile', file);
    const response = await api.patch<{ success: boolean; data: LegalPolicyVersionDto }>(
      `/master-admin/legal-policy-versions/${encodeURIComponent(id)}`,
      fd,
      { transformRequest: [stripMultipartContentType] }
    );
    return response.data;
  }

  const response = await api.patch<{ success: boolean; data: LegalPolicyVersionDto }>(
    `/master-admin/legal-policy-versions/${encodeURIComponent(id)}`,
    payload
  );
  return response.data;
}

export async function setLegalPolicyPublished(
  id: string,
  isPublished: boolean
): Promise<{ success: boolean; data: LegalPolicyVersionDto }> {
  const response = await api.patch<{ success: boolean; data: LegalPolicyVersionDto }>(
    `/master-admin/legal-policy-versions/${encodeURIComponent(id)}/published`,
    { isPublished }
  );
  return response.data;
}

export async function deleteLegalPolicyVersion(
  id: string
): Promise<{ success: boolean; message?: string }> {
  const response = await api.delete<{ success: boolean; message?: string }>(
    `/master-admin/legal-policy-versions/${encodeURIComponent(id)}`
  );
  return response.data;
}

export interface VendorPolicyAcceptanceVendorDetails {
  vendorId: string;
  email: string;
  phoneNumber: string;
  vendorRole: number;
  generatedId: string;
  status: string;
  isActive: boolean;
}

export interface VendorPolicyAcceptanceLegalSnippet {
  id: string;
  audience: LegalAudience;
  category: LegalCategory;
  scopedVendorType: number;
  version: number;
  title: string | null;
  isPublished: boolean;
  publishedAt: string | null;
}

export interface VendorPolicyAcceptanceRow {
  id: string;
  vendorId: string;
  policyType: string;
  vendorType: number | null;
  version: number;
  isAccepted: boolean;
  acceptedAt: string | null;
  isActive: boolean;
  legalPolicyVersionId: string | null;
  acceptedTitle: string | null;
  acceptedCategory: string | null;
  acceptedScopedVendorType: number | null;
  acceptedUserAgent: string | null;
  createdAt: string;
  updatedAt: string;
  vendor: VendorPolicyAcceptanceVendorDetails | null;
  legalPolicyVersion: VendorPolicyAcceptanceLegalSnippet | null;
}

export interface LegalPolicyAcceptanceStats {
  acceptedCount: number;
  expectedVendorCount: number | null;
  pendingVendorCount: number | null;
  acceptedDistinctVendors: number;
}

export interface ListVendorPolicyAcceptancesParams {
  vendorId?: string;
  legalPolicyVersionId?: string;
  policyType?: string;
  audience?: LegalAudience;
  category?: LegalCategory;
  isAccepted?: boolean;
  vendorType?: number;
  acceptedFrom?: string;
  acceptedTo?: string;
  limit?: number;
  offset?: number;
}

export async function fetchVendorPolicyAcceptances(
  params?: ListVendorPolicyAcceptancesParams
): Promise<{
  success: boolean;
  data: VendorPolicyAcceptanceRow[];
  pagination: { total: number; limit: number; offset: number };
}> {
  const res = await api.get('/master-admin/vendor-policy-acceptances', { params });
  return res.data;
}

export async function fetchAcceptancesForLegalPolicyVersion(
  id: string,
  params?: { limit?: number; offset?: number }
): Promise<{
  success: boolean;
  legalPolicyVersion: VendorPolicyAcceptanceLegalSnippet;
  data: VendorPolicyAcceptanceRow[];
  pagination: { total: number; limit: number; offset: number };
}> {
  const res = await api.get(
    `/master-admin/legal-policy-versions/${encodeURIComponent(id)}/acceptances`,
    { params }
  );
  return res.data;
}

export async function fetchAcceptanceStatsForLegalPolicyVersion(
  id: string
): Promise<{
  success: boolean;
  legalPolicyVersion: VendorPolicyAcceptanceLegalSnippet;
  stats: LegalPolicyAcceptanceStats;
}> {
  const res = await api.get(
    `/master-admin/legal-policy-versions/${encodeURIComponent(id)}/acceptance-stats`
  );
  return res.data;
}

export async function fetchPendingVendorsForLegalPolicyVersion(
  id: string,
  params?: { limit?: number; offset?: number }
): Promise<{
  success: boolean;
  data: VendorPolicyAcceptanceVendorDetails[];
  pagination: { total: number; limit: number; offset: number };
}> {
  const res = await api.get(
    `/master-admin/legal-policy-versions/${encodeURIComponent(id)}/pending-vendors`,
    { params }
  );
  return res.data;
}

export async function fetchVendorAcceptancesByVendor(
  vendorId: string
): Promise<{
  success: boolean;
  vendor: VendorPolicyAcceptanceVendorDetails & {
    isPolicyAccepted: boolean;
  };
  data: VendorPolicyAcceptanceRow[];
}> {
  const res = await api.get(
    `/master-admin/vendors/${encodeURIComponent(vendorId)}/policy-acceptances`
  );
  return res.data;
}

export interface VendorPolicyAcceptancesSummaryRow {
  legalPolicyVersionId: string;
  audience: LegalAudience;
  category: LegalCategory;
  scopedVendorType: number;
  version: number;
  title: string | null;
  isPublished: number | boolean;
  acceptedVendorCount: number;
}

export async function fetchVendorPolicyAcceptancesSummary(): Promise<{
  success: boolean;
  data: VendorPolicyAcceptancesSummaryRow[];
}> {
  const res = await api.get('/master-admin/vendor-policy-acceptances/summary');
  return res.data;
}

export const LEGAL_CATEGORY_LABELS: Record<LegalCategory, string> = {
  TERMS_AND_CONDITIONS: 'Terms & Conditions',
  PRIVACY_POLICY: 'Privacy Policy',
  CANCELLATION_AND_REFUND: 'Cancellation & Refund',
  SHIPPING_POLICY: 'Shipping Policy',
  VENDOR_REFUND_POLICY: 'Vendor Cancellation & Refund Policy',
};

export function formatScopedVendorType(v: number): string {
  if (v === LEGAL_POLICY_SCOPED_ALL) return 'All vendor types';
  const key = String(v);
  return vendorRoleMapping[key] ? `${vendorRoleMapping[key]} (${v})` : `Role ${v}`;
}
